import { Skeleton } from 'antd'
import type { JSX } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'

import { DIVIDER, MUTED_TEXT, TEXT } from '../../colors'
import { StyledCard, StyledCardHeader, StyledRowDescription } from '../Settings/styled'

import { ActivityRow } from 'api/activityLog'
import { ROUTES } from 'constants/routes'
import { useActivities } from 'screens/ActivityScreen/queries'
import { fmtRelative } from 'screens/ActivityScreen/utils'

const RECENT_CHANGES_LIMIT = 5

const StyledList = styled.ol`
  margin: 0;
  padding: 4px 0;
  list-style: none;
`

const StyledItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 12px 24px;

  & + & {
    border-top: 1px solid ${DIVIDER};
  }
`

const StyledItemHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
`

const StyledEvent = styled.span`
  min-width: 0;
  color: ${TEXT};
  font-size: 14px;
  overflow-wrap: anywhere;
`

const StyledTime = styled.time`
  flex: none;
  color: ${MUTED_TEXT};
  font-size: 13px;
  white-space: nowrap;
`

const StyledMessage = styled.div`
  padding: 16px 24px;
  color: ${MUTED_TEXT};
  font-size: 14px;
`

const StyledActivityLink = styled(Link)`
  color: ${TEXT};
  font-size: 14px;
  font-weight: 600;
  text-decoration: underline;
`

function actorLabel(actor: ActivityRow['actor']): string {
  if (!actor) return 'System'
  return actor.email || [actor.firstName, actor.lastName].filter(Boolean).join(' ') || actor.name || 'Unknown actor'
}

export function RecentChanges({ clientId }: { clientId: string }): JSX.Element {
  const { data, isLoading, isError } = useActivities({ client_id: [clientId], limit: RECENT_CHANGES_LIMIT })
  const rows = data?.data ?? []

  return (
    <StyledCard aria-labelledby="client-recent-changes">
      <StyledCardHeader>
        <h3 id="client-recent-changes">Recent Changes</h3>
        <StyledActivityLink to={`${ROUTES.ACTIVITY}?client_id=${encodeURIComponent(clientId)}`}>
          Activity log
        </StyledActivityLink>
      </StyledCardHeader>
      {isLoading && (
        <StyledMessage>
          <Skeleton active paragraph={{ rows: 3 }} title={false} />
        </StyledMessage>
      )}
      {isError && <StyledMessage>Could not load recent changes.</StyledMessage>}
      {!isLoading && !isError && rows.length === 0 && (
        <StyledMessage>No recorded activity for this client.</StyledMessage>
      )}
      {rows.length > 0 && (
        <StyledList>
          {rows.map((row) => (
            <StyledItem key={row.id}>
              <StyledItemHeader>
                <StyledEvent>{row.event}</StyledEvent>
                <StyledTime dateTime={row.occurredAt}>{fmtRelative(row.occurredAt)}</StyledTime>
              </StyledItemHeader>
              <StyledRowDescription>{actorLabel(row.actor)}</StyledRowDescription>
            </StyledItem>
          ))}
        </StyledList>
      )}
    </StyledCard>
  )
}
